import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Loader2, Play, Search } from "lucide-react";
import {
  MIKROBI_FAQ,
  categoryLabel,
  questionLabel,
  questionPrompt,
  type MikrobiQuestion,
} from "@/data/mikrobiFaq";
import { TOPIC_LABELS } from "@/lib/mikrobiTopics";
import MikrobiMessage from "@/components/mikrobi/MikrobiMessage";

interface Match {
  title: string;
  path: string;
  score: number;
  used: boolean;
}

interface CuratedMatch {
  id: string;
  title: string;
  keywords: string[];
  excerpt: string;
  score: number;
}

interface CheckResult {
  topic: string;
  complaints: Match[];
  articles: Match[];
  curated: CuratedMatch[];
  answer: string;
}

const FUNCTION_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/mikrobi-chat`;

const MatchList = ({ items, empty }: { items: Match[]; empty: string }) => {
  if (!items.length) return <p className="text-sm text-muted-foreground">{empty}</p>;
  return (
    <ul className="space-y-2">
      {items.map((m) => (
        <li key={m.path} className="flex items-start justify-between gap-3 text-sm">
          <div>
            <span className={m.used ? "font-medium" : "text-muted-foreground"}>{m.title}</span>
            <span className="block font-mono text-xs text-muted-foreground">{m.path}</span>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <span className="text-xs text-muted-foreground">Treffer {m.score}</span>
            <Badge variant={m.used ? "default" : "outline"}>
              {m.used ? "verwendet" : "nicht verwendet"}
            </Badge>
          </div>
        </li>
      ))}
    </ul>
  );
};

const AdminMikrobiCheck = () => {
  const [lang, setLang] = useState<"de" | "en">("de");
  const [text, setText] = useState("");
  const [filter, setFilter] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<CheckResult | null>(null);
  const [tested, setTested] = useState<string>("");

  const catalogue = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return MIKROBI_FAQ.map((c) => ({
      category: c,
      questions: c.questions.filter(
        (x) =>
          !q ||
          questionLabel(x, lang).toLowerCase().includes(q) ||
          questionPrompt(x, lang).toLowerCase().includes(q),
      ),
    })).filter((c) => c.questions.length > 0);
  }, [filter, lang]);

  const run = async (prompt: string) => {
    const trimmed = prompt.trim();
    if (!trimmed || loading) return;
    setLoading(true);
    setError(null);
    setResult(null);
    setTested(trimmed);
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData.session?.access_token;
      const res = await fetch(FUNCTION_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
          Authorization: `Bearer ${token ?? ""}`,
        },
        body: JSON.stringify({
          lang,
          debug: true,
          askedCount: 1,
          messages: [{ role: "user", content: trimmed }],
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data?.error ?? `Fehler ${res.status}`);
        return;
      }
      setResult(data as CheckResult);
    } catch (_e) {
      setError("Der Check ist gerade nicht erreichbar. Bitte später noch einmal versuchen.");
    } finally {
      setLoading(false);
    }
  };

  const runQuestion = (q: MikrobiQuestion) => run(questionPrompt(q, lang));

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto max-w-6xl space-y-8 px-4 py-10">
        <div>
          <Button variant="ghost" size="sm" asChild className="-ml-2 mb-2">
            <Link to="/admin/mikrobi">
              <ArrowLeft className="mr-2 h-4 w-4" /> Mikrobi-Auswertung
            </Link>
          </Button>
          <h1 className="text-3xl font-semibold">Fragenkatalog &amp; Abgleich-Check</h1>
          <p className="mt-1 max-w-3xl text-sm text-muted-foreground">
            Hier sehen Sie alle hinterlegten Start- und Folgefragen und können jede Anfrage testen.
            Der Check zeigt, welches Thema erkannt wurde, welche Beschwerdeseiten, Magazinartikel
            und gepflegten Antworten Mikrobi dafür herangezogen hat – und wie die Antwort aussieht.
          </p>
        </div>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">Eigene Anfrage testen</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex flex-wrap items-center gap-2">
              <Input
                value={text}
                onChange={(e) => setText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") run(text);
                }}
                placeholder="z. B. Ich habe seit Jahren Blähbauch und wiederkehrende Blasenentzündungen"
                className="min-w-[240px] flex-1"
                maxLength={1000}
              />
              <div className="flex gap-1">
                {(["de", "en"] as const).map((l) => (
                  <Button
                    key={l}
                    size="sm"
                    variant={lang === l ? "default" : "outline"}
                    onClick={() => setLang(l)}
                  >
                    {l.toUpperCase()}
                  </Button>
                ))}
              </div>
              <Button onClick={() => run(text)} disabled={loading || !text.trim()}>
                {loading ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Play className="mr-2 h-4 w-4" />
                )}
                Prüfen
              </Button>
            </div>
            {error && <p className="text-sm text-destructive">{error}</p>}
          </CardContent>
        </Card>

        {(loading || result) && (
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg">Ergebnis des Abgleichs</CardTitle>
              {tested && (
                <p className="text-sm text-muted-foreground">Getestete Anfrage: „{tested}“</p>
              )}
            </CardHeader>
            <CardContent className="space-y-6">
              {loading && (
                <p className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" /> Mikrobi prüft die Anfrage …
                </p>
              )}
              {result && (
                <>
                  <div className="flex flex-wrap items-center gap-2 text-sm">
                    <span className="text-muted-foreground">Erkanntes Thema:</span>
                    <Badge>{TOPIC_LABELS[result.topic] ?? result.topic}</Badge>
                  </div>

                  <div className="grid gap-6 lg:grid-cols-3">
                    <div className="space-y-2">
                      <h3 className="text-sm font-medium">Beschwerdeseiten</h3>
                      <MatchList items={result.complaints} empty="Keine Seite zugeordnet." />
                    </div>
                    <div className="space-y-2">
                      <h3 className="text-sm font-medium">Magazinartikel</h3>
                      <MatchList items={result.articles} empty="Kein Artikel zugeordnet." />
                    </div>
                    <div className="space-y-2">
                      <h3 className="text-sm font-medium">Gepflegte Antworten</h3>
                      {result.curated.length === 0 ? (
                        <p className="text-sm text-muted-foreground">
                          Noch keine eigenen Antworten hinterlegt.
                        </p>
                      ) : (
                        <ul className="space-y-2">
                          {result.curated.map((c) => (
                            <li key={c.id} className="text-sm">
                              <div className="flex items-center justify-between gap-2">
                                <span className={c.score > 0 ? "font-medium" : "text-muted-foreground"}>
                                  {c.title}
                                </span>
                                <span className="shrink-0 text-xs text-muted-foreground">
                                  Treffer {c.score}
                                </span>
                              </div>
                              <p className="text-xs text-muted-foreground">{c.excerpt}</p>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <h3 className="text-sm font-medium">Antwort von Mikrobi</h3>
                    <div className="rounded-lg border border-border bg-muted/30 px-4 py-3">
                      <MikrobiMessage content={result.answer} />
                    </div>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        )}

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">Alle hinterlegten Fragen</CardTitle>
            <div className="relative mt-2 max-w-sm">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                placeholder="Fragen durchsuchen …"
                className="pl-9"
              />
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            {catalogue.map(({ category, questions }) => (
              <div key={category.id} className="space-y-2">
                <h3 className="text-sm font-semibold">{categoryLabel(category, lang)}</h3>
                <ul className="space-y-2">
                  {questions.map((q) => (
                    <li
                      key={q.id}
                      className="flex flex-wrap items-start justify-between gap-3 rounded-lg border border-border px-3 py-2"
                    >
                      <div className="min-w-[200px] flex-1">
                        <p className="text-sm font-medium">{questionLabel(q, lang)}</p>
                        <p className="mt-1 text-xs text-muted-foreground">
                          Gesendeter Text: {questionPrompt(q, lang)}
                        </p>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => runQuestion(q)}
                        disabled={loading}
                      >
                        <Play className="mr-2 h-3.5 w-3.5" /> Testen
                      </Button>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
            {catalogue.length === 0 && (
              <p className="text-sm text-muted-foreground">Keine Frage gefunden.</p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminMikrobiCheck;
